import { Headphones, Radio } from "lucide-react";
import KineticMarquee from "@/components/KineticMarquee";
import { SOCIAL_LINKS, APPLE_PODCAST_ID } from "@/data/content";

export default function Audio() {
  return (
    <div data-testid="audio-page" className="px-6 lg:px-12 py-16 lg:py-24 text-[var(--tsws-green)]">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-8">
          <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Audio Vault</span>
          <h1 className="font-display text-7xl md:text-8xl lg:text-[10rem] mt-3 leading-[0.82]">
            press<br />
            <span className="text-stroke">play.</span>
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="font-tag text-xl">Ears in. Pints up.</p>
          <p className="mt-3 leading-relaxed opacity-85">
            Driving, walking, sinking schooners — the show in your ears. Pick your weapon below.
          </p>
        </div>
      </div>

      <KineticMarquee phrases={["audio only", "ears in", "on the go", "every platform"]} />

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-16">
        {/* Apple */}
        <div data-testid="apple-player-block">
          <div className="flex items-center gap-3 mb-4">
            <Headphones className="text-[var(--tsws-cigar)]" />
            <h2 className="font-display text-4xl">apple podcasts</h2>
          </div>
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-2 shadow-brutal-sm">
            <iframe
              title="Apple Podcasts player"
              allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
              frameBorder="0"
              height="450"
              style={{ width: "100%", overflow: "hidden", background: "transparent" }}
              src={`https://embed.podcasts.apple.com/au/podcast/the-sesh-with-sav/id${APPLE_PODCAST_ID}`}
            />
          </div>
          <a
            href={SOCIAL_LINKS.apple}
            target="_blank"
            rel="noreferrer"
            data-testid="apple-open-link"
            className="inline-block mt-4 font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
          >
            open in apple podcasts →
          </a>
        </div>

        {/* Spotify */}
        <div data-testid="spotify-player-block">
          <div className="flex items-center gap-3 mb-4">
            <Radio className="text-[var(--tsws-cigar)]" />
            <h2 className="font-display text-4xl">spotify</h2>
          </div>
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-2 shadow-brutal-sm">
            <iframe
              title="Spotify player"
              src="https://open.spotify.com/embed/show/0Qx5DKpRDuyEsl0v7QyXz1?utm_source=generator&theme=0"
              width="100%"
              height="450"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ background: "transparent" }}
            />
          </div>
          <a
            href={SOCIAL_LINKS.spotify}
            target="_blank"
            rel="noreferrer"
            data-testid="spotify-open-link"
            className="inline-block mt-4 font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
          >
            open in spotify →
          </a>
        </div>
      </div>

      {/* Other platforms */}
      <div className="mt-24 border-t-2 border-[var(--tsws-green)] pt-12">
        <span className="font-label text-xs opacity-70">⏤ Other Platforms</span>
        <div className="mt-6 flex flex-wrap gap-4">
          {[
            { label: "google podcasts", href: SOCIAL_LINKS.apple },
            { label: "pocket casts", href: SOCIAL_LINKS.apple },
            { label: "overcast", href: SOCIAL_LINKS.apple },
            { label: "rss feed", href: SOCIAL_LINKS.apple },
          ].map((p) => (
            <a
              key={p.label}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`audio-${p.label.replace(/\s+/g, "-")}`}
              className="font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
            >
              {p.label} →
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
